import React from 'react';
import { mediaQueries, styled } from '@components-shared/src/stitches';
import { NextFutureImage } from '@components/main-components/image/NextFutureImage';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { LOADING_STYLES } from '@components/main-components/skeleton/Skeleton';
import { MediaQuery } from '@context/mediaQuery';
import { EmptySection } from './AccordionDetails';
import { useChartCardContext } from './Context';

export const CHART_CARD_COLLECTOR_IMAGE_SIZES = {
  collapsed: {
    mobile: 24,
    tablet: 36,
    desktop: 48,
  },
  expanded: {
    mobile: 100,
    tablet: 100,
    desktop: 100,
  },
} as const;

export const COLLECTOR_COLUMN_SIZES = {
  mobile: 24,
  tablet: 88,
} as const;

export const COLLECTOR_COLUMN_GAPS = {
  mobile: 8,
  tablet: 12,
} as const;

export const EXPANDED_MOBILE_AVATAR_AMOUNT = 12;

const Collector = ({
  src,
  summary = false,
  unoptimized,
}: {
  /**
   * Image source
   * @example https://unsplash.com/photos/1qyvW7gRcFQ
   */
  src?: string;
  /**
   * If true, will render collectors smaller to fit in collapsed chart card
   */
  summary?: boolean;
  /**
   * MAKE SURE YOU KNOW WHAT YOU ARE DOING.
   *
   * This was added so that we can render the image in storybook preview.
   * Only scenario you would want to use this is if you are trying to render GIFs
   *
   * But if you are thinking to render GIFs would HIGHLY recommend consulting with the team first.
   */
  unoptimized?: boolean;
}) => {
  const { isLoadingSummary, isLoadingDetails } = useChartCardContext();
  const state = summary ? 'collapsed' : 'expanded';
  const isLoading = (summary && isLoadingSummary) || (!summary && isLoadingDetails);
  const { tablet } = MediaQuery.useContainer();
  const size = tablet ? 'tablet' : 'mobile';

  return (
    <AvatarContainer summary={summary}>
      <ImageWrapper loading={isLoading} summary={summary}>
        {!isLoading && src && (
          <NextFutureImage
            fill
            src={src}
            alt="Chart card collector"
            unoptimized={unoptimized}
            width={CHART_CARD_COLLECTOR_IMAGE_SIZES[state][size]}
            height={CHART_CARD_COLLECTOR_IMAGE_SIZES[state][size]}
            // an exception to our size flag since this is an optimization of the images we fetch rather than visual looks
            sizes={`${CHART_CARD_COLLECTOR_IMAGE_SIZES[state].mobile}px, ${mediaQueries['tablet']} ${CHART_CARD_COLLECTOR_IMAGE_SIZES[state].tablet}px, ${mediaQueries['desktop']} ${CHART_CARD_COLLECTOR_IMAGE_SIZES[state].desktop}px`}
          />
        )}
      </ImageWrapper>
    </AvatarContainer>
  );
};

export function ChartCardCollectors({
  avatars,
  summary = false,
  empty = false,
  pathnameUri = '/',
  collectorAmount = 3,
}: {
  /**
   * Collectors to render. JSX.Element instead of ReactNode to avoid padding arrays,
   * which will crash storybook when using controls
   */
  avatars: JSX.Element;
  /**
   * If true, will render collectors smaller to fit in collapsed chart card
   */
  summary?: boolean;
  /**
   * If true, will render empty state
   */
  empty?: boolean;
  /**
   * Release Uri to point
   */
  pathnameUri?: Pathname;
  /**
   * Use this to avoid css grid wrapping based on breakpoint
   * Which is faster than renders caused by media queries
   */
  collectorAmount?: number;
}) {
  const { isLoadingSummary } = useChartCardContext();
  const isLoading = isLoadingSummary;
  const { tablet, hasAppMounted } = MediaQuery.useContainer();
  const size = tablet || !hasAppMounted ? 'tablet' : 'mobile';

  return (
    <Container loading={isLoading}>
      {empty && !isLoading ? (
        <EmptySection>
          No collectors.{' '}
          <AccessibleLink pathnameUri={pathnameUri}>Be the first to collect</AccessibleLink>
        </EmptySection>
      ) : (
        <AvatarsContainer
          summary={summary}
          size={size}
          css={{
            gridTemplateColumns: `repeat(${
              summary ? collectorAmount : EXPANDED_MOBILE_AVATAR_AMOUNT / 2
            }, ${summary ? `${COLLECTOR_COLUMN_SIZES[size]}px` : '1fr'})`,
          }}
        >
          {avatars}
        </AvatarsContainer>
      )}
    </Container>
  );
}

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  flexShrink: 0,
});

const Container = styled('div', {
  display: 'flex',
  position: 'relative',
  pointerEvents: 'all',
  flexShrink: 0,

  variants: {
    loading: {
      true: {
        pointerEvents: 'none',
      },
    },
  },
});

const AvatarsContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: `repeat(12, ${COLLECTOR_COLUMN_SIZES.mobile}px)`,
  flexShrink: 0,
  gap: COLLECTOR_COLUMN_GAPS.mobile,

  compoundVariants: [
    {
      size: 'mobile',
      summary: true,
      css: {
        margin: 0,
      },
    },
    {
      size: 'mobile',
      summary: false,
      css: {
        margin: 0,
        width: '100%',
      },
    },
    {
      size: 'tablet',
      summary: false,
      css: {
        margin: 0,
      },
    },
  ],

  variants: {
    size: {
      mobile: {
        gap: COLLECTOR_COLUMN_GAPS.mobile,
      },
      tablet: {
        gap: COLLECTOR_COLUMN_GAPS.tablet,
      },
    },
    summary: {
      false: {
        width: '100%',
      },
    },
  },
});

const tabletStyles = {
  width: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['tablet'],
  height: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['tablet'],

  '@desktop': {
    width: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['desktop'],
    height: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['desktop'],
  },
};

const AvatarContainer = styled('div', {
  flexShrink: 0,
  cursor: 'pointer',
  transition: 'box-shadow 0.4s ease',
  overflow: 'hidden',
  borderRadius: '$full',
  // https://github.com/google/model-viewer/issues/662
  // Fixes the issue where border radius was not applying in Safari
  transform: 'translateZ(0)',

  variants: {
    summary: {
      true: {
        width: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['mobile'],
        height: CHART_CARD_COLLECTOR_IMAGE_SIZES['collapsed']['mobile'],

        '@tablet': tabletStyles,
      },
      false: {
        width: '100%',
        height: '100%',

        '@tablet': tabletStyles,
      },
    },
  },

  '&:hover': {
    lift: 'secondary',

    'img, svg': {
      transform: 'scale(1.2)',
    },
  },
});

const ImageWrapper = styled('div', {
  // https://github.com/google/model-viewer/issues/662
  // Fixes the issue where border radius was not applying in Safari
  transform: 'translateZ(0)',
  overflow: 'hidden',
  width: '100%',
  height: '100%',
  background: LOADING_STYLES.background, // Render background color while loading image
  aspectRatio: '1 / 1',

  'img, svg': {
    transition: 'transform 0.4s ease',
    objectFit: 'cover',
  },

  variants: {
    loading: {
      true: {
        ...LOADING_STYLES,
      },
    },
    summary: {
      true: {},
    },
  },
});

ChartCardCollectors.Wrapper = Wrapper;
ChartCardCollectors.Collector = Collector;
ChartCardCollectors.CHART_CARD_COLLECTOR_IMAGE_SIZES = CHART_CARD_COLLECTOR_IMAGE_SIZES;
ChartCardCollectors.AvatarContainer = AvatarContainer;
ChartCardCollectors.ImageWrapper = ImageWrapper;
